import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Formik } from 'formik';
import * as Yup from 'yup';

const Register = () => {
  const { signup } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    nama: Yup.string().required('Nama lengkap wajib diisi'),
    email: Yup.string()
      .email('Email tidak valid')
      .required('Email wajib diisi'),
    password: Yup.string()
      .required('Password wajib diisi')
      .min(6, 'Password minimal 6 karakter'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Password harus sama')
      .required('Konfirmasi password wajib diisi'),
    role: Yup.string().required('Pilih peran Anda'),
    nim: Yup.string().when('role', {
      is: 'mahasiswa',
      then: () => Yup.string().required('NIM wajib diisi'),
      otherwise: () => Yup.string()
    }),
    prodi: Yup.string().when('role', {
      is: 'mahasiswa',
      then: () => Yup.string().required('Program studi wajib diisi'),
      otherwise: () => Yup.string()
    }),
    nip: Yup.string().when('role', {
      is: 'dosen',
      then: () => Yup.string().required('NIP wajib diisi'),
      otherwise: () => Yup.string()
    }),
    fakultas: Yup.string().when('role', {
      is: 'dosen',
      then: () => Yup.string().required('Fakultas wajib diisi'),
      otherwise: () => Yup.string()
    }),
    jurusan: Yup.string().when('role', {
      is: 'dosen',
      then: () => Yup.string().required('Jurusan wajib diisi'),
      otherwise: () => Yup.string()
    })
  });

  const handleSubmit = async (values) => {
    try {
      setError('');
      setLoading(true);
      
      // Siapkan data pengguna berdasarkan peran
      const userData = {
        nama: values.nama,
        role: values.role
      };
      
      if (values.role === 'mahasiswa') {
        userData.nim = values.nim;
        userData.prodi = values.prodi;
      } else if (values.role === 'dosen') {
        userData.nip = values.nip;
        userData.fakultas = values.fakultas;
        userData.jurusan = values.jurusan;
      }
      
      const result = await signup(values.email, values.password, userData, values.role);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <div className="w-100" style={{ maxWidth: "700px", margin: "0 auto" }}>
        <Card>
          <Card.Body>
            <h2 className="text-center mb-4">Pendaftaran Akun</h2>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
              <Alert variant="success">
                Pendaftaran berhasil! Silakan cek email Anda untuk verifikasi dan tunggu persetujuan dari Penanggung Jawab Tugas Akhir.
                Anda akan dialihkan ke halaman login dalam beberapa detik.
              </Alert>
            )}
            
            <Formik
              initialValues={{
                nama: '',
                email: '',
                password: '',
                confirmPassword: '',
                role: '',
                nim: '',
                prodi: '',
                nip: '',
                fakultas: '',
                jurusan: ''
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nama Lengkap</Form.Label>
                    <Form.Control
                      type="text"
                      name="nama"
                      value={values.nama}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.nama && errors.nama}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.nama}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.email && errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.password && errors.password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Konfirmasi Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="confirmPassword"
                          value={values.confirmPassword}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.confirmPassword && errors.confirmPassword}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.confirmPassword}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Peran</Form.Label>
                    <Form.Select
                      name="role"
                      value={values.role}
                      onChange={(e) => {
                        setFieldValue('role', e.target.value);
                      }}
                      onBlur={handleBlur}
                      isInvalid={touched.role && errors.role}
                    >
                      <option value="">Pilih Peran</option>
                      <option value="mahasiswa">Mahasiswa</option>
                      <option value="dosen">Dosen</option>
                      <option value="pj">Penanggung Jawab (Admin)</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.role}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
                  {values.role === 'mahasiswa' && (
                    <>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>NIM</Form.Label>
                            <Form.Control
                              type="text"
                              name="nim"
                              value={values.nim}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.nim && errors.nim}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.nim}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Program Studi</Form.Label>
                            <Form.Control
                              type="text"
                              name="prodi"
                              value={values.prodi}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.prodi && errors.prodi}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.prodi}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                  
                  {values.role === 'dosen' && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>NIP</Form.Label>
                        <Form.Control
                          type="text"
                          name="nip"
                          value={values.nip}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.nip && errors.nip}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.nip}
                        </Form.Control.Feedback>
                      </Form.Group>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Fakultas</Form.Label>
                            <Form.Control
                              type="text"
                              name="fakultas"
                              value={values.fakultas}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.fakultas && errors.fakultas}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.fakultas}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Jurusan</Form.Label>
                            <Form.Control
                              type="text"
                              name="jurusan"
                              value={values.jurusan}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.jurusan && errors.jurusan}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.jurusan}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                  
                  <Button disabled={loading} className="w-100 mt-3" type="submit">
                    {loading ? 'Memproses...' : 'Daftar'}
                  </Button>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
        
        <div className="w-100 text-center mt-2">
          Sudah punya akun? <Link to="/login">Login</Link>
        </div>
      </div>
    </Container>
  );
};

export default Register;
